import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/lib/api";
import { useHubModuleHasAnyAccess } from "@/lib/useHubModuleWrite";
import {
  fetchOpenInventoryDiscrepancySkus,
  OPEN_INVENTORY_DISCREPANCY_SKUS_QUERY_KEY,
} from "@/pages/manufacturing/inventoryDiscrepancyApi";

function normalizeSkuKey(sku: string): string {
  return sku.trim().toLowerCase();
}

/**
 * Batch lookup for SKUs with open inventory discrepancy requests (pending or investigating).
 * Disabled when the user lacks manufacturing management read access.
 */
export function useOpenInventoryDiscrepancySkus(skus: string[]) {
  const canView = useHubModuleHasAnyAccess("manufacturing_management");

  const uniqueSkus = useMemo(
    () => [...new Set(skus.map((sku) => sku.trim()).filter((sku) => sku.length > 0))],
    [skus],
  );

  const query = useQuery<{ open_skus: string[] }, ApiError>({
    queryKey: [...OPEN_INVENTORY_DISCREPANCY_SKUS_QUERY_KEY, uniqueSkus.join("\n")],
    queryFn: () => fetchOpenInventoryDiscrepancySkus(uniqueSkus),
    enabled: canView && uniqueSkus.length > 0,
    staleTime: 60_000,
  });

  const openSkuKeys = useMemo(() => {
    const set = new Set<string>();
    for (const sku of query.data?.open_skus ?? []) {
      const key = normalizeSkuKey(sku);
      if (key.length > 0) set.add(key);
    }
    return set;
  }, [query.data?.open_skus]);

  const hasOpenDiscrepancy = useMemo(
    () =>
      (sku: string): boolean => {
        if (!canView) return false;
        const key = normalizeSkuKey(sku);
        return key.length > 0 && openSkuKeys.has(key);
      },
    [canView, openSkuKeys],
  );

  return {
    canView,
    hasOpenDiscrepancy,
    isLoading: canView && uniqueSkus.length > 0 && (query.isPending || query.isFetching),
    query,
  };
}
